import { createDebug } from '@/utils/debug'

// AHA-46751: what this frame's binding USED to point at, carried INSIDE the binding itself
// (document.settings `dataSlidePPT`) rather than in a side table.
//
// A present-run reset on the server clones the presentation and moves every slide id (see
// handleGetCorrentSlideIdByMultipleSession). Two stores then disagree: document.settings is
// remapped on the next cold load, while the binding TAG on the host slide keeps the id it was
// written with. The auto-start ownership check compares exactly those two, so without a record of
// the move a reset makes every bound frame read "the shown slide is somebody else's" and stop
// starting.
//
// Why settings and not a localStorage map keyed by document:
//
//   1. There is no document url to key by. Office.context.document.url is empty on the PC host
//      until the deck has been saved to a path, so the map was a silent no-op on the ONLY
//      platform that reads it (the ownership check is desktop-only).
//   2. The history travels with the file. Settings live inside the .pptx, so a deck remapped on
//      one machine and opened on another still knows its own history; a localStorage map does not
//      cross machines, and the remap API cannot rebuild it because it answers "same slide" for an
//      id that is already current.
//   3. It is per frame, which is all this question needs. On desktop each cloned add-in frame
//      carries its own settings snapshot, baked in at export time (runDesktopInsert) — the same
//      property the whole per-frame binding rests on. (Settings are shared live across frames on
//      the WEB, so this history would be useless there — but the web ownership check needs no
//      history at all: it compares two RAW pre-remap bindings, which a reset moves together.)
//   4. It costs no extra write. The remap already persists the moved binding; this is one more
//      field on that same payload.
//
// A frame only ever asks "is the shown tag MY slide?", so it only needs its OWN former ids —
// another frame's stale id resolving to nothing yields OTHER, which is the correct verdict anyway.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:slide-remap' (or 'aha:ppt:*').
const log = createDebug('ppt:slide-remap')

// Bounds what a binding can accumulate over many present-runs, newest last. Ten is far more than
// the repair needs: the first confirmed match rewrites the stale tag to the current id
// (healTagsAtSlideIfUnchanged), so an entry is normally consumed by the very next Slide Show
// rather than left to age.
export const PREVIOUS_SLIDE_ID_LIMIT = 10

// The history a binding should carry after `oldSlideId` moved. Ids are normalised to strings
// (the remap API answers with numbers, tags are written with String()) and de-duplicated, so an
// id that comes round again is re-dated rather than stored twice.
export const appendPreviousSlideId = (previousSlideIds, oldSlideId) => {
  const existing = (Array.isArray(previousSlideIds) ? previousSlideIds : []).map(String)
  const next = existing.filter((id) => id !== String(oldSlideId))
  next.push(String(oldSlideId))
  return next.slice(-PREVIOUS_SLIDE_ID_LIMIT)
}

// The same binding with no history on it. Used to enforce that ONLY the present-run remap may
// carry one: every other write of dataSlidePPT replaces the binding outright, and a history
// describing the binding it replaced would go on vouching for slides this frame no longer owns.
export const withoutSlideIdHistory = (binding) => {
  if (!binding || binding.previousSlideIds === undefined) {
    return binding
  }
  const { previousSlideIds, ...rest } = binding
  return rest
}

// Does `slideId` name something this binding used to point at? Compared as strings for the same
// reason everything else on this path is: tags are strings, settings hold numbers, and a shape
// mismatch would read as "not mine" and suppress a legitimate start.
export const matchesPreviousSlideId = (binding, slideId) => {
  const previousSlideIds = binding && binding.previousSlideIds
  if (!slideId || !Array.isArray(previousSlideIds) || !previousSlideIds.length) {
    return false
  }
  const matched = previousSlideIds.some((id) => String(id) === String(slideId))
  log('history lookup', slideId, '| binding was', previousSlideIds, '| matched =', matched)
  return matched
}
